// @ts-strict-ignore
import { AxiosError } from 'axios';
import ReactHotToast from 'react-hot-toast';
import { useRouteMatch } from 'react-router-dom';

import { useOverlayState } from '@/components/AppOverlay';
import { Button } from '@/components/Button/Button';
import { usePublicSourceDelinkMutation } from '@/components/ConnectApps/Common/publicSourceMutations';
import { useIsOverlayPreview } from '@/components/Preview/hooks/useIsOverlayPreview';
import { useIsSourceConnected } from '@/components/SourceCards/hooks/useIsSourceConnected';
import { Events, useCustomEventDispatch } from '@/hooks/customEvent';
import { useGetFilteredPublicApps } from '@/hooks/query/useFetchPublicApps';
import { useNavigateToNextItemUrl } from '@/hooks/useNavigateToNextItemUrl';
import { useNavigate } from '@/router';
import type { RouteParam } from '@/types';
import { getAppGroupTitle } from '@/utils/getAppTitle';
import { SourceUnfollowToast } from '@/utils/toasts';

export const UnFollowButton = ({ sourceId, label }: unFollowType) => {
  const unFollowMutation = usePublicSourceDelinkMutation();
  const navigateToNextItem = useNavigateToNextItemUrl();
  const isOverlayPreview = useIsOverlayPreview();
  const isFullPreview = useRouteMatch('/preview');
  const refreshFeed = useCustomEventDispatch(Events.FEED_REFRESH);
  const navigate = useNavigate();

  const { closeOverlay } = useOverlayState();
  const match = useRouteMatch<RouteParam>('/:category/:source/:subCategory');
  const category = match?.params?.category;
  const source = match?.params?.source;

  const { isLoading: isSourceLoading, isConnected } =
    useIsSourceConnected(sourceId);

  const {
    isLoading: isPublicAppsLoading,
    rssData,
    exchangesData,
  } = useGetFilteredPublicApps(true);

  const handleNavigation = async () => {
    if (isOverlayPreview) {
      closeOverlay();
      if (category === 'apps') {
        navigateToNextItem(rssData);
      } else {
        refreshFeed();
      }
    } else if (isFullPreview) {
      navigate.replace('/');
    } else if (category === 'apps') {
      if (
        [
          'india_regulatory_data',
          'us_regulatory_data',
          'nz_regulatory_data',
        ].includes(source)
      ) {
        navigateToNextItem(exchangesData);
      } else {
        navigateToNextItem(rssData);
      }
    }
  };

  const unFollowPublicSource = () => {
    unFollowMutation.mutate(sourceId, {
      onSuccess: () => {
        const groupTitle = getAppGroupTitle(sourceId);
        SourceUnfollowToast(groupTitle || label);
        handleNavigation();
      },
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const errorMessage =
            error?.response?.data?.error ||
            'Something went wrong. Please try again';
          ReactHotToast.error(errorMessage);
        }
      },
    });
  };

  if (isSourceLoading || isPublicAppsLoading || !isConnected) {
    return null;
  }

  return (
    <Button
      className='flex-shrink-0'
      variant='error'
      size='extrasmall'
      label='Unfollow'
      loading={unFollowMutation.isLoading}
      onClick={unFollowPublicSource}
    />
  );
};

type unFollowType = {
  sourceId: string;
  label?: string;
};
